import { Component, Output, EventEmitter, Input } from '@angular/core';
import { NotificationService } from 'src/app/service/notification.service';
import { Event } from 'src/app/model/event';
import { SubmissionStatus } from 'src/app/enum/submission.status';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { forkJoin } from 'rxjs';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { SubmissionRanking } from '../submissions-list-table-v2/submissions-list-table-v2.component';
import { SubmissionWithdrawalComponent } from '../submission-withdrawal/submission-withdrawal.component';
import { SubmissionDeleteComponent } from '../submission-delete/submission-delete.component';
import { Submission } from 'src/app/model/paper';
import { VerifySimilarityDialogComponent} from '../verify-similarity-dialog/verify-similarity-dialog.component';
import { FilesService } from 'src/app/service/files.service';
import { File } from '../../model/file';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, switchMap } from 'rxjs/operators';
import { AdminService } from 'src/app/service/admin.service';

@Component({
  selector: 'app-menu-submission-list',
  templateUrl: './menu-submission-list.component.html',
  styleUrls: ['./menu-submission-list.component.scss']
})
export class MenuSubmissionListComponent {
  public SubmissionStatus = SubmissionStatus;

  @Input() public event: Event;
  @Input() public submissions: Array<SubmissionRanking>;
  @Input() public similarityVerification: boolean;

  // Quando true, renderiza as ações numa barra inferior (app-floating-submit-bar)
  // em vez do menu "..." (mat-menu). Reusa toda a lógica de ações deste componente.
  @Input() public barMode: boolean = false;

  // Só o botão "Copiar" na barra. Usado pelo Status de publicação (a Lista de
  // Submissões em modo `publication`): ali a seleção serve para exportar, não
  // para agir sobre as submissões — alterar status, retirar e excluir são ações
  // da Lista, e não pertencem àquela tela.
  @Input() public copyOnly: boolean = false;

  @Output() public changedStatusSubmissions: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public submissionsDeleted: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public submissionsSelect: EventEmitter<boolean> = new EventEmitter<boolean>();
  // Copiar as linhas selecionadas — quem implementa a cópia é o componente da tabela
  // (tem o builder do HTML); a barra só sinaliza a ação.
  @Output() public copySelected: EventEmitter<void> = new EventEmitter<void>();

  // Rótulo do atalho de cópia, sensível à plataforma: ⌘C no macOS, Ctrl+C nos demais.
  // (O evento `copy` dispara nos dois; aqui só ajustamos o texto exibido no botão.)
  public readonly copyShortcut: string =
    /Mac|iPhone|iPad|iPod/i.test((typeof navigator !== 'undefined' && (navigator.platform || navigator.userAgent)) || '')
      ? '⌘C' : 'Ctrl+C';

  // Feedback visual do copy: o botão "Copiar" troca pra ícone check + "Copiado!" por ~1s
  // após cada cópia bem-sucedida (MESMO padrão do app-selection-actions-bar da página
  // Submissões do autor/revisor). Disparado pelo pai (tabela) via flashCopied(), pra cobrir
  // tanto o clique no botão quanto o CTRL+C. Substitui a antiga notificação verde.
  public copied = false;
  private copiedTimeoutId: any = null;

  public flashCopied(): void {
    this.copied = true;
    if (this.copiedTimeoutId) { clearTimeout(this.copiedTimeoutId); }
    this.copiedTimeoutId = setTimeout(() => { this.copied = false; this.copiedTimeoutId = null; }, 1000);
  }

  constructor(
    private notificationService: NotificationService,
    private filesService: FilesService,
    private router: Router,
    private dialog: MatDialog,
    private submissionsService: SubmissionsService,
    private translateService: TranslateService,
    private adminService: AdminService
  ) { }

  public changeStatusSubmissions($status: string): void {
    switch ($status) {
      case SubmissionStatus.ACTIVE:
        this.openDialog(SubmissionStatus.ACTIVE);
        break;
      case SubmissionStatus.ACCEPTED:
        this.openDialog(SubmissionStatus.ACCEPTED);
        break;
      case SubmissionStatus.REJECTED:
        this.openDialog(SubmissionStatus.REJECTED);
        break;
      case SubmissionStatus.PENDING:
        this.openDialog(SubmissionStatus.PENDING);
        break;
      case SubmissionStatus.WITHDRAWN:
        this.openWithdrawnDeleteDialog(SubmissionStatus.WITHDRAWN);
        break;
      case SubmissionStatus.DELETED:
        this.openWithdrawnDeleteDialog(SubmissionStatus.DELETED);
        break;
      default:
        break;
    }
  }

  public openDialog(status: SubmissionStatus): void {
    const data = {
      title: `submissions.ranking.select-submission-${status}`,
      content: ''
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data });

    dialogRef.afterClosed().subscribe(confirmation => {
      if (confirmation) {
        this.submitStatusSubmissions(status);
      }
    });

  }

  private submitStatusSubmissions(status: SubmissionStatus): void {
    forkJoin(
      this.submissionsSelected.map(s => {
        s.submission.status = status;
        return this.submissionsService.editSubmission({ status }, s.submission.id);
    }))
      .subscribe(() => {
        this.notify(status);
        this.setStatusSubmissions(status);
      });
  }

  private setStatusSubmissions(status: SubmissionStatus): void {
    this.submissionsSelected.forEach(({ submission }) => submission.status = status);
    this.changedStatusSubmissions.emit(true);
  }

  private openWithdrawnDeleteDialog(status: SubmissionStatus): void {
    const dialogConfig = new MatDialogConfig();

    const submissions = this.submissionsSelected.map(s => s.submission);

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.data = { submission: null , submissions };

    let dialogRef: MatDialogRef<SubmissionWithdrawalComponent | SubmissionDeleteComponent, MatDialogConfig>;

    if (status === SubmissionStatus.WITHDRAWN) {
      dialogRef = this.dialog.open(SubmissionWithdrawalComponent, dialogConfig);
    } else {
      dialogRef = this.dialog.open(SubmissionDeleteComponent, dialogConfig);
    }

    dialogRef.afterClosed().subscribe((response) => {
      if (response && status === SubmissionStatus.WITHDRAWN) {
        this.setStatusSubmissions(SubmissionStatus.WITHDRAWN);
        return;
      }

      this.submissionsDeleted.emit((response as Submission[]).map(submission => submission.id));
    });
  }

  public changeRoute(): void {
    const submissionsIDPending: Array<number> = this.submissionsIDPending;

    const ids = JSON.stringify(submissionsIDPending);

    this.router.navigate([`/admin/${this.event.id}/submissions/notify-pending`], { queryParams: { ids: ids, eventID: this.event.id }});
  }

  private get submissionsIDPending(): Array<number> {
    const submissionsPending: Array<SubmissionRanking> = this.submissions.filter((s: SubmissionRanking) => {
      if (s.selected && s.submission.status === SubmissionStatus.PENDING) {
        return s.submission.id;
      }
    });

    return submissionsPending.map((s: SubmissionRanking) => s.submission.id);
  }

  private get submissionsSelected(): SubmissionRanking[] {
    return this.submissions.filter((s: SubmissionRanking) => s.selected);
  }

  private notify(status: SubmissionStatus): void {
    const message = `reports.submissions.ranking.successful-assign-${status}`;

    this.notificationService.notify(message, { params: { total: this.submissionsSelected.length } });
  }


  public openVerifySimilarityDialog() {
    const selectedSubmissions = this.submissions.filter(ss => ss.selected && ss.submission);
  
    if (selectedSubmissions.length > 0) {
      const dialogRef = this.dialog.open(VerifySimilarityDialogComponent, {
        data: { 
          rankedSubmissions: selectedSubmissions, 
          tracks: this.event.tracks 
        }
      });
  
      dialogRef.afterClosed().pipe(
        filter((files: File[]) => !!files && files.length > 0),
        switchMap((files: File[]) => {
          this.adminService.progressBar.start();
          return forkJoin(
            files.map(file => {
              return this.filesService.submitFileToSimilarityCheck(file).pipe( 
                map((result) => {
                  if (result) {
                    file.submittedToVerification = true; 
                  }
                  return result;
                })
              );
            })
          );
        })
      ).subscribe(
        () => {
          this.adminService.progressBar.stop();
        },
        (error) => {
          this.adminService.progressBar.stop();
          this.notificationService.notifyError(this.translateService.instant(`errors.failed-file-verification`));
        }
      );
    } else {
      this.notificationService.notifyError(this.translateService.instant(`errors.no-submissions-selected`));
    }
  }

  public get AtLeastOneSelection() : Boolean {
    return this.submissionsSelected.length > 0;
  }

  public disabledTooltip(){
    if (!this.AtLeastOneSelection) {
      return this.translateService.instant('forms.buttons.validation.selectAtLeastOne');
    }

    return "";
  }
  
  public selectAllLines(): void{
    this.submissions.map(submission =>{
      submission.selected = true;
    })
    this.submissionsSelect.emit(true);
  }
  public deselectAllLines(): void{
    this.submissions.map(submission =>{
      submission.selected = false;
    })
    this.submissionsSelect.emit(false);
  }
}
